<template>
  <div class="tw-pb-4 lg:tw-pb-0">
    <EntityDetailSkeleton v-if="isLoading" />

    <!-- Header -->
    <EntityDetailHeader
      v-if="!isLoading"
      :eyebrow="t('payments.titles.payment_details')"
      :title="payment.payment_number || t('payments.titles.payment_details')"
      :has-prev="Boolean(payment?.previous_payment?.id)"
      :has-next="Boolean(payment?.next_payment?.id)"
      :prev-label="t('common.forms.previous')"
      :next-label="t('common.forms.next')"
      @prev="navigateToPayment(payment?.previous_payment?.id)"
      @next="navigateToPayment(payment?.next_payment?.id)"
    >
      <template #status>
        <Badge :variant="getStatusBadgeVariant()">{{ getPaymentStatusDisplay() }}</Badge>
        <Badge v-if="payment.is_clarification_requested" variant="clarification">
          {{ t('orders.titles.clarification_requested') }}
        </Badge>
      </template>

      <template #actions>
        <Button v-if="showEditPaymentBtn()" variant="outline" size="sm" @click="editPayment()">
          <Pencil class="tw-h-4 tw-w-4" />
          {{ t('common.forms.edit') }}
        </Button>

        <CustomerActionButton
          v-if="showApproveOrRejectAction()"
          :loading="isApproving"
          :class="[actionButtonClass, 'tw-bg-emerald-600 tw-text-white hover:tw-bg-emerald-700']"
          @click="approvePayment(payment.id)"
        >
          <CheckCircle class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.approve') }}
        </CustomerActionButton>
        <CustomerActionButton
          v-if="showApproveOrRejectAction()"
          :loading="isRejecting"
          :class="[actionButtonClass, 'tw-bg-red-500 tw-text-white hover:tw-bg-red-500/90']"
          @click="rejectPayment(payment.id)"
        >
          <XCircle class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.reject') }}
        </CustomerActionButton>
        <CustomerActionButton
          v-if="showRequestClarificationAction()"
          :class="[
            actionButtonClass,
            'tw-border tw-border-amber-500 tw-text-amber-600 hover:tw-bg-amber-50 dark:tw-text-amber-400',
          ]"
          @click="RequestClarification(payment.id)"
        >
          <MessageCircleQuestion class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.request_clarification') }}
        </CustomerActionButton>

        <DropdownMenu
          v-if="
            showRespondToClarificationAction() ||
            (payment.status_display === 'DRAFT' && payment.released_by?.id === currentUser?.id)
          "
        >
          <DropdownMenuTrigger as-child>
            <Button variant="outline" size="sm">
              {{ t('common.forms.actions') }}
              <ChevronDown class="tw-h-4 tw-w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="tw-w-60">
            <DropdownMenuItem
              v-if="showRespondToClarificationAction()"
              @click="respondToClarification(payment.id)"
            >
              <CheckCircle2 class="tw-mr-2 tw-h-4 tw-w-4 tw-text-surface-500" />
              {{ t('orders.actions.mark_clarifications_completed') }}
            </DropdownMenuItem>
            <template
              v-if="
                payment.status_display === 'DRAFT' && payment.released_by?.id === currentUser?.id
              "
            >
              <DropdownMenuSeparator />
              <DropdownMenuItem
                class="tw-text-red-600 focus:tw-text-red-600 dark:tw-text-red-400"
                @click="isDeleteDialogOpen = true"
              >
                <Trash2 class="tw-mr-2 tw-h-4 tw-w-4" />
                {{ t('common.forms.delete') }}
              </DropdownMenuItem>
            </template>
          </DropdownMenuContent>
        </DropdownMenu>
      </template>
    </EntityDetailHeader>

    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:open="isDeleteDialogOpen">
      <DialogContent class="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle>{{ t('common.forms.delete') }}</DialogTitle>
          <DialogDescription>{{ t('orders.titles.confirm_delete') }}?</DialogDescription>
        </DialogHeader>
        <DialogFooter class="tw-flex tw-gap-3 sm:tw-justify-end">
          <Button variant="outline" @click="isDeleteDialogOpen = false">
            {{ t('common.forms.cancel') }}
          </Button>
          <Button variant="destructive" @click="onChangePaymentDelete">
            {{ t('common.forms.delete') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Main Content -->
    <EntitySplitLayout
      v-if="!isLoading"
      storage-key="payment-split-document-size"
      :min-document-size="50"
      :max-document-size="67"
    >
      <template #documents>
        <DocumentManagerPanel :files="payment.files || []" :show-heading="false" />
      </template>
      <template #details>
        <EntityDetailTabs
          :details-label="t('payments.titles.payment_details')"
          :show-related="false"
          :comments-unread="commentsUnread"
          @seen-comments="markCommentsSeen"
        >
          <template #details>
            <PaymentDetailsCard :payment="payment" />
            <section v-if="payment.orders?.length">
              <div class="tw-mb-4 tw-flex tw-items-center tw-gap-2">
                <Link2 class="tw-h-4 tw-w-4 tw-text-secondary-500 dark:tw-text-neutral-400" />
                <h2
                  class="tw-font-heading tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-secondary-700 dark:tw-text-neutral-300"
                >
                  {{ t('orders.titles.related_orders') }}
                </h2>
              </div>
              <Accordion type="single" collapsible :default-value="'order-0'" class="tw-space-y-2">
                <AccordionItem
                  v-for="(paymentOrder, index) in payment.orders"
                  :key="index"
                  :value="`order-${index}`"
                  class="tw-overflow-hidden tw-rounded-xl tw-border tw-border-surface-200 dark:tw-border-neutral-800"
                >
                  <AccordionTrigger
                    class="tw-px-4 tw-py-3 hover:tw-no-underline data-[state=open]:tw-bg-surface-50 dark:data-[state=open]:tw-bg-neutral-900/40"
                  >
                    <router-link
                      v-if="paymentOrder.order_id"
                      :to="{ name: 'order', params: { id: paymentOrder.order_id } }"
                      class="tw-font-semibold tw-text-secondary-700 hover:tw-text-secondary-900 dark:tw-text-neutral-200 hover:tw-underline tw-underline-offset-2"
                      @click.stop
                    >
                      {{ paymentOrder.order_number }}
                    </router-link>
                    <span
                      v-else
                      class="tw-font-semibold tw-text-secondary-900 dark:tw-text-neutral-100"
                    >
                      {{ paymentOrder.order_number }}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent class="tw-px-4 tw-pb-4">
                    <dl class="tw-space-y-0.5">
                      <div class="tw-flex tw-justify-between tw-gap-6 tw-py-1.5 tw-text-sm">
                        <dt class="tw-text-surface-500 dark:tw-text-neutral-400">
                          {{ t('payment.titles.payment_amount') }}
                        </dt>
                        <dd class="tw-font-semibold tw-text-secondary-900 dark:tw-text-neutral-100">
                          {{ formatCurrency(paymentOrder?.payment_amount) }}
                        </dd>
                      </div>
                      <div class="tw-flex tw-justify-between tw-gap-6 tw-py-1.5 tw-text-sm">
                        <dt class="tw-text-surface-500 dark:tw-text-neutral-400">
                          {{ t('orders.titles.current_balance') }}
                        </dt>
                        <dd class="tw-font-semibold tw-text-secondary-900 dark:tw-text-neutral-100">
                          {{ formatCurrency(paymentOrder?.balance) }}
                        </dd>
                      </div>
                      <div class="tw-flex tw-justify-between tw-gap-6 tw-py-1.5 tw-text-sm">
                        <dt class="tw-text-surface-500 dark:tw-text-neutral-400">
                          {{ t('orders.titles.amount') }}
                        </dt>
                        <dd class="tw-font-semibold tw-text-secondary-900 dark:tw-text-neutral-100">
                          {{ formatCurrency(paymentOrder?.order_amount) }}
                        </dd>
                      </div>
                      <div
                        v-if="paymentOrder.status !== 'APPROVED'"
                        class="tw-flex tw-justify-between tw-gap-6 tw-py-1.5 tw-text-sm"
                      >
                        <dt class="tw-text-surface-500 dark:tw-text-neutral-400">
                          {{ t('orders.titles.amount_after_approval') }}
                        </dt>
                        <dd class="tw-font-semibold tw-text-secondary-900 dark:tw-text-neutral-100">
                          {{ formatCurrency(paymentOrder?.amount_after_payment) }}
                        </dd>
                      </div>
                      <div
                        v-if="paymentOrder.status !== 'APPROVED'"
                        class="tw-flex tw-justify-between tw-gap-6 tw-py-1.5 tw-text-sm"
                      >
                        <dt class="tw-text-surface-500 dark:tw-text-neutral-400">
                          {{ t('payments.titles.balance_after_approval') }}
                        </dt>
                        <dd class="tw-font-semibold tw-text-secondary-900 dark:tw-text-neutral-100">
                          {{ formatCurrency(paymentOrder?.balance_after_payment) }}
                        </dd>
                      </div>
                    </dl>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>
          </template>
          <template #comments>
            <PaymentComments :payment="payment" :payment-id="paymentId" />
          </template>
          <template #notes>
            <PaymentSelfNotes :payment-id="paymentId" />
          </template>
        </EntityDetailTabs>
      </template>
    </EntitySplitLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { usePaymentStore } from '@/stores/payment.store';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useUnreadComments } from '@/composables/useUnreadComments';

// UI Components
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Icons
import {
  ChevronDown,
  Pencil,
  Trash2,
  CheckCircle,
  CheckCircle2,
  XCircle,
  MessageCircleQuestion,
  Link2,
} from 'lucide-vue-next';

// Child Components
import PaymentDetailsCard from '@/components/payments/PaymentDetailsCard.vue';
import PaymentComments from '@/components/payments/PaymentComments.vue';
import PaymentSelfNotes from '@/components/payments/PaymentSelfNotes.vue';
import { DocumentManagerPanel } from '@/components/shared/documents';
import EntityDetailTabs from '@/components/shared/entity-detail/EntityDetailTabs.vue';
import EntitySplitLayout from '@/components/shared/entity-detail/EntitySplitLayout.vue';
import EntityDetailHeader from '@/components/shared/entity-detail/EntityDetailHeader.vue';
import EntityDetailSkeleton from '@/components/shared/entity-detail/EntityDetailSkeleton.vue';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';

// Shared styling for the inline header action buttons (matches shadcn `size="sm"`).
const actionButtonClass =
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-h-8 tw-px-3 tw-text-xs tw-font-medium tw-shadow-sm tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-neutral-950 dark:focus-visible:tw-ring-neutral-300';

const router = useRouter();
const { t } = useI18n();
const { handleApiError } = useApiUtils();
const { setBreadcrumbs } = useBreadcrumbs();

const {
  getPaymentById,
  getPaymentComments,
  markPaymentCommentsRead,
  deletePayment,
  paymentsBulkAction,
  respondToClarificationRequest,
} = usePaymentStore();

const { currentUser } = storeToRefs(useUserStore());

const {
  hasUnread: commentsUnread,
  check: checkUnreadComments,
  markSeen: markCommentsSeen,
} = useUnreadComments();

const payment = ref<any>({
  project: null,
  customer: null,
  amount: null,
  status: 'PENDING_APPROVAL',
  status_display: '',
  files: [],
  orders: [],
  is_clarification_requested: false,
});

const paymentId = ref<number | null>(null);
const isDeleteDialogOpen = ref(false);
const isApproving = ref(false);
const isRejecting = ref(false);

const formatCurrency = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);
};

const navigateToPayment = (id: number | null) => {
  if (id) {
    router.push({ name: 'payment', params: { id } });
  }
};

const editPayment = () => {
  router.push({ name: 'edit-payment', params: { id: payment.value.id } });
};

const approvePayment = async (id: number) => {
  isApproving.value = true;
  try {
    await handleApiError(async () => {
      await paymentsBulkAction('bulk_approve', [id]);
      payment.value = await getPaymentById(id);
    }, t('payments.titles.successful_approval'));
  } finally {
    isApproving.value = false;
  }
};

const RequestClarification = async (id: number) => {
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_request_clarification', [id]);
    payment.value = await getPaymentById(id);
  }, t('payments.titles.successful_clarification'));
};

const respondToClarification = async (id: number) => {
  await handleApiError(async () => {
    await respondToClarificationRequest(id);
    payment.value = await getPaymentById(id);
  }, t('payments.titles.successful_clarification_completed'));
};

const rejectPayment = async (id: number) => {
  isRejecting.value = true;
  try {
    await handleApiError(async () => {
      await paymentsBulkAction('bulk_reject', [id]);
      payment.value = await getPaymentById(id);
    }, t('payments.titles.successful_rejection'));
  } finally {
    isRejecting.value = false;
  }
};

const onChangePaymentDelete = async () => {
  isDeleteDialogOpen.value = false;
  await handleApiError(async () => {
    await deletePayment(payment.value.id);
  }, t('payments.titles.successful_delete'));
  router.push({ name: 'payments' });
};

const getStatusBadgeVariant = (): BadgeVariant => {
  const statusMap: Record<string, BadgeVariant> = {
    APPROVED: 'approved',
    CLEARING: 'clearing',
    REJECTED: 'rejected',
    DRAFT: 'draft',
    PENDING_APPROVAL: 'pending',
  };
  return statusMap[payment.value.status_display] || 'secondary';
};

const getPaymentStatusDisplay = () => {
  if (payment.value.status_display === 'APPROVED') {
    return t('orders.titles.approved');
  } else if (payment.value.status_display === 'REJECTED') {
    return t('orders.titles.rejected');
  } else if (payment.value.status_display === 'DRAFT') {
    return t('orders.titles.draft');
  } else if (payment.value.status_display === 'PENDING_APPROVAL') {
    return t('requests.titles.pending');
  } else if (payment.value.status_display === 'CLEARING' && !payment.value.is_restrict) {
    return t('orders.titles.clearing');
  } else if (payment.value.status_display === 'CLEARING' && payment.value.is_restrict) {
    return `${t('orders.titles.clearing')} (${payment.value.total_clearer_approved || 0} / ${
      payment.value.total_clearer || 0
    })`;
  }
  return payment.value.status_display;
};

const showEditPaymentBtn = () => {
  return (
    (payment.value.status === 'DRAFT' && payment.value.released_by?.id === currentUser.value?.id) ||
    (payment.value.is_clarification_requested &&
      payment.value.created_by_id === currentUser.value?.id)
  );
};

const showApproveOrRejectAction = () => {
  if (currentUser.value?.user_role.includes('Admin')) {
    return ['PENDING_APPROVAL', 'CLEARING'].includes(payment.value.status_display);
  }
  if (currentUser.value?.user_role.includes('Approver')) {
    return payment.value.status_display === 'PENDING_APPROVAL';
  }
  if (currentUser.value?.user_role.includes('Payment Clearer')) {
    return payment.value.status_display === 'PENDING_APPROVAL';
  }
  return false;
};

const showRequestClarificationAction = () => {
  if (
    currentUser.value?.user_role.includes('Admin') ||
    currentUser.value?.user_role.includes('Approver') ||
    currentUser.value?.user_role.includes('Order Clearer')
  ) {
    return (
      ['PENDING_APPROVAL', 'CLEARING'].includes(payment.value.status_display) &&
      !payment.value.is_clarification_requested
    );
  }
  return false;
};

const showRespondToClarificationAction = () => {
  return (
    payment.value.is_clarification_requested &&
    (currentUser.value?.id === payment.value.created_by_id ||
      currentUser.value?.id === payment.value.requested_clarification_by_id)
  );
};

const isLoading = ref(true);

onMounted(async () => {
  paymentId.value = +router.currentRoute.value.params.id;
  try {
    payment.value = await getPaymentById(paymentId.value);

    setBreadcrumbs([
      { labelKey: 'sidebar.items.payments', to: { name: 'payments' } },
      { label: payment.value.payment_number },
    ]);
    checkUnreadComments(paymentId.value, getPaymentComments, markPaymentCommentsRead);
  } finally {
    isLoading.value = false;
  }
});

watch(
  () => router.currentRoute.value.params.id,
  async newVal => {
    if (newVal) {
      payment.value = await getPaymentById(newVal);
      setBreadcrumbs([
        { labelKey: 'sidebar.items.payments', to: { name: 'payments' } },
        { label: payment.value.payment_number },
      ]);
    }
  }
);
</script>
